import { Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { useEffect, useState } from 'react'
import {
  ArrayField,
  Create,
  Datagrid,
  FunctionField,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useNotify,
  useRefresh,
} from 'react-admin'
import { SOURCE_NAME_CHOICES } from 'shared/helpers/constants'

import { captureExceptionIfProduction } from 'helpers/utils'
import { postRequest, putRequest } from '../../api/common'
import { deleteConnectedIntegration } from '../../api/core'
import { detectEnvironment, Environment } from '../../config/config'
import { IntegrationStatuses } from '../../helpers/constants'
import { getVGSUrl } from '../../helpers/utils'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'
import { ProductSubscription, ProductSubscriptionBundleName } from '../../models/subscription'

function getCredType(credential: any) {
  switch (credential.status) {
    case IntegrationStatuses.Connected:
      return 'admin'
    case IntegrationStatuses.Pending:
      if (credential?.username?.match('cloudtrucks')) {
        return 'admin'
      } else {
        return 'user'
      }

    default:
      return 'user'
  }
}

const TeamConnectedIntegrations = (props: any) => {
  const isFlex = props.record.product_subscription.product_name === ProductSubscription.FLEX

  if (!isFlex) {
    return (
      <Typography variant="body1" component="span">
        This tab is available only for flex teams.
      </Typography>
    )
  }

  return (
    <>
      <EditCredentialsDialogButton {...props} team={props.record} newCredentials={true} variant="contained">
        + Add Credential
      </EditCredentialsDialogButton>
      <ArrayField {...props} source="connected_integrations">
        <Datagrid>
          <TextField label="Broker" source="name" />
          <FunctionField
            label="User Credentials"
            render={(record: any) => {
              if (record.status === IntegrationStatuses.Pending) {
                return (
                  <ShowCredentialsDialogButton record={record} team={props.record}>
                    User's Credentials
                  </ShowCredentialsDialogButton>
                )
              } else {
                return null
              }
            }}
          />
          <FunctionField
            label="CT Admin Credentials"
            render={(record: any) => {
              if (record.status === IntegrationStatuses.Connected) {
                return (
                  <ShowCredentialsDialogButton record={record} team={props.record}>
                    Saved Credentials
                  </ShowCredentialsDialogButton>
                )
              } else {
                return null
              }
            }}
          />
          <FunctionField
            label="Status"
            render={(record: any) => {
              if (record.status === IntegrationStatuses.Pending) {
                return (
                  <EditCredentialsDialogButton record={record} team={props.record}>
                    Confirm
                  </EditCredentialsDialogButton>
                )
              } else {
                return `${record.status}`
              }
            }}
          />
          <RemoveCredentialsDialogButton record={props.record} />
        </Datagrid>
      </ArrayField>
    </>
  )
}

const ShowCredentialsDialogButton = (props: any) => {
  const { record, team, requestIsLoading, credsType } = props
  const [open, setOpen] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const dialogTitle =
    credsType === 'user' ? `User's Credentials: ${record.name}` : `CT Admin's Credentials: ${record.name}`
  return (
    <>
      <Button variant="text" color="primary" onClick={() => setOpen(true)}>
        {props.children}
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>Carrier: {team.name}</span>
          </DialogContentText>
          <DialogContentText>
            <span>Username: {record.username}</span>
          </DialogContentText>
          <DialogContentText>
            <span>Password: {revealed ? <RevealedCredentials {...props} /> : '***********'}</span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setRevealed(!revealed)}>
            {revealed ? 'Hide Password' : 'Reveal Password'}
          </Button>
          <Button color="secondary" onClick={() => setOpen(false)} disabled={requestIsLoading}>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const EditCredentialsDialogButton = (props: any) => {
  const { record, newCredentials, children } = props
  const [open, setOpen] = useState(false)
  const credsType = getCredType(record)
  let dialogTitle

  if (newCredentials) {
    dialogTitle = 'New Credentials'
  } else {
    dialogTitle =
      credsType === 'user' ? `User's Credentials: ${record.name}` : `CT Admin's Credentials: ${record.name}`
  }

  return (
    <>
      <Button variant={props.variant || 'text'} color="primary" onClick={() => setOpen(true)}>
        {children}
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <CredentialsCreate {...props} />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const SaveButtonAltered = (props: any) => {
  const { newCredentials, record, team, isFlexStarterCoyote } = props
  const notify = useNotify()
  const refresh = useRefresh()
  const apiUrl = `${getVGSUrl()}/api/v1/console/connected-integrations/`
  const url = newCredentials ? apiUrl : `${apiUrl}${record.id}/`
  const method = newCredentials ? postRequest : putRequest
  const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true

  const handleClick = (values: any) => {
    const status = newCredentials ? IntegrationStatuses.Pending : IntegrationStatuses.Connected

    const payload: any = {
      team: team.id,
      status: status,
      name: values.name,
    }

    if (isFlexStarterCoyote) {
      payload.source_id = values.source_id
      payload.api_key_encrypted = values.api_key_encrypted
    } else {
      payload.username = values.username
      payload.password_encrypted = values.password_encrypted
    }

    method(url, payload, { withCredentials })
      .then(() => {
        notify('Credential created', { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(error.response?.data.detail, { type: 'error' })
        captureExceptionIfProduction(error)
      })
  }

  return <SaveButton {...props} onSave={handleClick} />
}
const CredentialsCreateToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButtonAltered label="Save" submitOnEnter={false} variant="text" {...props} />
  </Toolbar>
)
const CredentialsCreate = (props: any) => {
  const { newCredentials, record, team } = props
  record.password_encrypted = ''
  record.username = ''
  record.api_key_encrypted = ''
  record.source_id = ''

  const flexStarterCoyote =
    team.product_subscription.bundle_name === ProductSubscriptionBundleName.FLEX &&
    props.record.name === 'coyote'

  const isFlexStarterCoyote = { isFlexStarterCoyote: flexStarterCoyote }

  return (
    <Create record={record} resource="connected-integrations" basePath="/connected-integrations">
      <SimpleForm toolbar={<CredentialsCreateToolbar {...props} {...isFlexStarterCoyote} />}>
        {newCredentials ? (
          <SelectInput source="name" label="Broker" validate={required()} choices={SOURCE_NAME_CHOICES} />
        ) : null}
        {flexStarterCoyote ? (
          <TextInput source="source_id" label="Client Id" validate={required()} />
        ) : (
          <TextInput source="username" label="Username" validate={required()} />
        )}
        {flexStarterCoyote ? (
          <TextInput source="api_key_encrypted" label="Production" validate={required()} />
        ) : (
          <TextInput source="password_encrypted" label="Password" validate={required()} />
        )}
      </SimpleForm>
    </Create>
  )
}

const RevealedCredentials = ({ record }: any) => {
  const [loading, setLoading] = useState(true)
  const [password, setPassword] = useState(null)
  const url = `${getVGSUrl()}/api/v1/console/connected-integrations/${record.id}/reveal/`
  const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true

  useEffect(() => {
    postRequest(url, null, { withCredentials }).then((data) => {
      setLoading(false)
      setPassword(data.password_encrypted)
    })
  })

  if (loading) {
    return <>Loading...</>
  } else {
    return <span>{password}</span>
  }
}

const RemoveCredentialsDialogButton = ({ record }: any) => {
  const [open, setOpen] = useState(false)
  const refresh = useRefresh()

  const handleClick = () => {
    return deleteConnectedIntegration(record.id).then(() => {
      setOpen(false)
      refresh()
    })
  }

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Remove Link
      </Button>
      <Dialog open={open} onClose={withDisableBackdropClick(() => setOpen(false))}>
        <DialogTitle>Are you sure you want to remove the {record.name} link?</DialogTitle>
        <DialogActions>
          <Button color="primary" onClick={handleClick}>
            Remove
          </Button>
          <Button color="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TeamConnectedIntegrations
